import React, { useMemo, useState } from "react";
import DropdownPill from "../utils/DropdownPill";

const d = (iso) => new Date(iso + "T00:00:00");
const startOfWeekSun = (date) => {
  const out = new Date(date);
  out.setDate(out.getDate() - ((out.getDay() + 7) % 7));
  out.setHours(0, 0, 0, 0);
  return out;
};
const addDays = (date, days) => {
  const out = new Date(date);
  out.setDate(out.getDate() + days);
  return out;
};
const clampDate = (val, min, max) => (val < min ? min : val > max ? max : val);
const sameDay = (a, b) =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

// 🎨 Color map based on leave type
const TYPE_COLORS = {
  "Sick Leave": "#16A34A", // green
  "Emergency": "#EF4444", // red
  "Vacation": "#4F46E5", // blue
  "Annual Leave": "#9333EA", // purple
  "Maternity Leave": "#D97706", // orange
  "Paternity Leave": "#0D9488", // teal
  Default: "#4F46E5", // fallback
};

function getEventColor(type, status) {
  const base = TYPE_COLORS[type] || TYPE_COLORS.Default;

  // lighten/darken based on status
  if (status === "Pending") return `${base}90`; // slightly transparent
  if (status === "Rejected") return "#9CA3AF"; // gray
  return base;
}

function buildWeekSegments(events, weekStart) {
  const weekEnd = addDays(weekStart, 6);
  const segments = [];

  for (const ev of events) {
    const evStart = d(ev.start);
    const evEnd = d(ev.end);
    if (evEnd < weekStart || evStart > weekEnd) continue;

    const segStart = clampDate(evStart, weekStart, weekEnd);
    const segEnd = clampDate(evEnd, weekStart, weekEnd);

    const colStart = segStart.getDay() + 1;
    const colEnd = segEnd.getDay() + 2;

    segments.push({
      ...ev,
      weekStart,
      colStart,
      colEnd,
      startDate: segStart,
      endDate: segEnd,
      length: colEnd - colStart,
    });
  }

  const lanes = [];
  for (const seg of segments.sort(
    (a, b) => a.colStart - b.colStart || b.length - a.length
  )) {
    let placed = false;
    for (let i = 0; i < lanes.length; i++) {
      if (seg.colStart >= lanes[i]) {
        seg.lane = i;
        lanes[i] = seg.colEnd;
        placed = true;
        break;
      }
    }
    if (!placed) {
      seg.lane = lanes.length;
      lanes.push(seg.colEnd);
    }
  }

  return { segments, laneCount: lanes.length };
}

export const PTOCalendarPro = ({
  data: rawData = [],
  anchorDate: anchor = new Date(),
}) => {
  const [mode, setMode] = useState("month");
  const [anchorDate, setAnchorDate] = useState(anchor);
  const [employee, setEmployee] = useState("All");

  // 🧩 Convert backend data → internal event model
  const events = useMemo(
    () =>
      rawData.map((item) => ({
        id: item.id,
        name: item.emp_name,
        type: item.request_type,
        start: item.start_date,
        end: item.end_date,
        status: item.status,
        role: item.emp_role,
        color: getEventColor(item.request_type, item.status),
      })),
    [rawData]
  );

  // Layout constants
  const headerHeightPx = 40;
  const cellHeightPx = 128;
  const laneHeightPx = 22;
  const laneGapPx = 6;

  const month = anchorDate.getMonth();
  const year = anchorDate.getFullYear();
  const monthStart = new Date(year, month, 1);
  const gridStart = startOfWeekSun(monthStart);
  const gridDays = mode === "month" ? 42 : 7;
  const weeks = mode === "month" ? 6 : 1;

  const allDays = useMemo(
    () => [...Array(gridDays)].map((_, i) => addDays(gridStart, i)),
    [gridStart, gridDays]
  );

  const names = useMemo(
    () => ["All", ...Array.from(new Set(events.map((e) => e.name)))],
    [events]
  );

  const employeeOptions = useMemo(
    () => names.map((n) => ({ label: n, value: n })),
    [names]
  );

  const filtered = useMemo(
    () => (employee === "All" ? events : events.filter((e) => e.name === employee)),
    [employee, events]
  );

  const weekStarts = [...Array(weeks)].map((_, w) => addDays(gridStart, w * 7));
  const weekData = useMemo(
    () => weekStarts.map((ws) => buildWeekSegments(filtered, ws)),
    [filtered, gridStart, weeks]
  );

  const monthLabel = anchorDate.toLocaleDateString("en-US", {
    month: "long",
    year: "numeric",
  });

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4">
      {/* Top Bar */}
      <div className="mb-3 flex items-center justify-between gap-3">
        <DropdownPill
          options={employeeOptions}
          value={employee}
          onChange={setEmployee}
        />
        <h2 className="text-xl font-semibold text-gray-900">{monthLabel}</h2>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setMode("month")}
            className={`w-24 rounded-md px-3 py-2 text-sm ${
              mode === "month"
                ? "bg-[#1D2951] text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            Month
          </button>
          <button
            onClick={() => setMode("week")}
            className={`w-24 rounded-md px-3 py-2 text-sm ${
              mode === "week"
                ? "bg-[#1D2951] text-white"
                : "bg-gray-100 text-gray-700"
            }`}
          >
            Week
          </button>
        </div>
      </div>

      {/* Calendar Grid */}
      <div className="relative">
        <div className="grid grid-cols-7 border-t border-l">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => (
            <div
              key={d}
              className="border-b border-r bg-gray-50 py-2 text-center text-sm font-medium text-gray-700"
            >
              {d}
            </div>
          ))}

          {allDays.map((day, i) => {
            const isCurMonth = day.getMonth() === month;
            const isToday = sameDay(day, new Date());
            return (
              <div
                key={i}
                className={`relative h-32 border-b border-r p-1 ${
                  isCurMonth ? "bg-white" : "bg-gray-50"
                }`}
              >
                <div
                  className={`absolute right-2 top-1 rounded px-1 text-xs ${
                    isToday ? "bg-[#1D2951] text-white" : "text-gray-600"
                  }`}
                >
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Overlay Event Bars */}
        <div className="absolute inset-x-0 top-0">
          {weekStarts.map((ws, wi) => {
            const rowTopPx = headerHeightPx + wi * cellHeightPx;
            const { segments } = weekData[wi];
            return (
              <div key={wi} className="relative" style={{ height: 0 }}>
                {segments.map((seg) => {
                  const topPx = Math.round(
                    rowTopPx + 32 + seg.lane * (laneHeightPx + laneGapPx)
                  );
                  const dayCount = seg.colEnd - seg.colStart;
                  return Array.from({ length: dayCount }).map((_, i) => {
                    const col = seg.colStart + i - 1;
                    const leftPct = (col / 7) * 100;
                    const widthPct = 100 / 7;
                    const isStart = i === 0;
                    const isEnd = i === dayCount - 1;

                    return (
                      <div
                        key={`${seg.id}-${col}`}
                        style={{
                          position: "absolute",
                          left: `${leftPct}%`,
                          width: `calc(${widthPct}% - 4px)`,
                          top: `${topPx}px`,
                          height: `${laneHeightPx + 10}px`,
                          backgroundColor: seg.color,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          boxSizing: "border-box",
                          overflow: "hidden",
                          borderRadius:
                            isStart && isEnd
                              ? "6px"
                              : isStart
                              ? "6px 0 0 6px"
                              : isEnd
                              ? "0 6px 6px 0"
                              : "0",
                        }}
                        title={`${seg.name} — ${seg.type} (${seg.status})`}
                      >
                        {isStart && (
                          <div
                            style={{
                              padding: "4px 8px",
                              fontSize: "11px",
                              fontWeight: 500,
                              color: "#fff",
                              lineHeight: 1.1,
                              display: "flex",
                              flexDirection: "column",
                              justifyContent: "center",
                              alignItems: "flex-start",
                            }}
                          >
                            <span style={{ whiteSpace: "nowrap", marginBottom: "2px" }}>
                              {seg.name}
                            </span>
                            <span style={{ opacity: 0.9, fontSize: "10px" }}>
                              {seg.type}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  });
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
